import { Drawer as BaseDrawer } from "@base-ui/react/drawer";
import { useDrawerShell, DrawerPopupContents } from "./DrawerShared";
import type { TypeDrawerProps } from "./DrawerTypes";
import type { CSSProperties } from "react";

// Utility for merging class names properly. Copied from ButtonTailwind.tsx —
// `cn` is NOT exported from @sproutsocial/seeds-react-utilities, so each
// Tailwind component carries its own copy.
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

/**
 * Tailwind/CSS-class shell. The preferred path — routed to by `DrawerHybrid`
 * whenever the consumer passes no styled-system props. Renders the real Base UI
 * primitives (Backdrop/Viewport/Popup) with component-scoped classes from
 * `drawer.css`; the runtime numerics (`width`, `offset`, `zIndex`) are applied
 * as direct inline styles so drag/swipe/snap/portal behaviour is preserved.
 */
const DrawerTailwind = (props: TypeDrawerProps) => {
  const shell = useDrawerShell(props);
  const {
    effectiveDirection,
    hasSnapPoints,
    hasActionRail,
    rootProps,
    rest,
    width,
    offset,
    zIndex,
    id,
    open,
  } = shell;

  // `className` and `style` are public <div> props (TypeDrawerProps extends
  // ComponentPropsWithoutRef<"div">) that arrive via `rest`. Pull them out so
  // they MERGE with the popup's computed classes/geometry instead of
  // overwriting them — matching the styled-components path, where a consumer
  // className is appended to the generated class and an inline style wins over
  // the generated width.
  const { className, style, ...domRest } = rest as {
    className?: string;
    style?: CSSProperties;
  } & Record<string, unknown>;

  return (
    <BaseDrawer.Root {...rootProps}>
      <BaseDrawer.Portal>
        <BaseDrawer.Backdrop
          data-testid="drawer-backdrop"
          className="seeds-drawer-backdrop"
        />
        <BaseDrawer.Viewport
          className={cn("seeds-drawer-viewport", {
            "seeds-drawer-viewport--snap": hasSnapPoints,
          })}
        >
          <BaseDrawer.Popup
            initialFocus
            data-qa-drawer={id}
            data-qa-drawer-isopen={open}
            {...domRest}
            className={cn(
              "seeds-drawer-popup",
              effectiveDirection === "bottom"
                ? "seeds-drawer-popup--bottom"
                : "seeds-drawer-popup--side",
              effectiveDirection === "right" && "seeds-drawer-popup--right",
              effectiveDirection === "left" && "seeds-drawer-popup--left",
              {
                "seeds-drawer-popup--snap": hasSnapPoints,
                "seeds-drawer-popup--action-rail": hasActionRail,
              },
              // Consumer className folded in last so it appends to (not
              // replaces) the component classes.
              className
            )}
            style={{
              // z-index is always inline (runtime numeric, mirrors styles.ts).
              zIndex,
              // Side drawers carry their width/offset as direct inline styles —
              // runtime numerics, and required for the jsdom `toHaveStyle`
              // assertions. Bottom drawers derive width/position from the class.
              ...(effectiveDirection === "right"
                ? { width: `${width}px`, right: `${offset}px` }
                : effectiveDirection === "left"
                ? { width: `${width}px`, left: `${offset}px` }
                : {}),
              // Consumer style folded in last so it wins over the computed
              // geometry, matching the styled-components inline-style semantics.
              ...style,
            }}
          >
            {effectiveDirection === "bottom" && (
              <div aria-hidden className="seeds-drawer-drag-handle" />
            )}
            <DrawerPopupContents shell={shell} />
          </BaseDrawer.Popup>
        </BaseDrawer.Viewport>
      </BaseDrawer.Portal>
    </BaseDrawer.Root>
  );
};

export default DrawerTailwind;
